// The server lists behind the preview's shape line, CTA and period count —
// the SAME queries (keys and fetchers) the shape steps hold, so a step's write
// (setQueryData / invalidate) shows in the panel at once, and the done screen
// reads them back (from the cache or the server). Rule 7: nothing without them.

import { useQueries, useQuery } from '@tanstack/react-query';
import type { BookingShape } from '../../data/api';
import { getBands, listPeriods, listPlaces, listPrograms, listSessions, listTables, listUnitTypes } from '../../data/shapeApi';
import type { ShapeLists } from '../../preview';

export const shapeKeys = {
  places: (b: string) => ['onboarding', 'places', b],
  bands: (b: string) => ['onboarding', 'bands', b],
  periods: (b: string) => ['onboarding', 'periods', b],
  unitTypes: (b: string) => ['onboarding', 'unit-types', b],
  programs: (b: string) => ['onboarding', 'programs', b],
  sessions: (b: string, p?: string) => (p ? ['onboarding', 'sessions', b, p] : ['onboarding', 'sessions', b]),
  tables: (b: string) => ['onboarding', 'tables', b],
};

const opts = { staleTime: Infinity, retry: 0 } as const;

/** The lists of `shape` for the business (nothing before the business exists, or for staff). */
export function useShapeLists(shape: BookingShape | null, businessId: string | null, enabled: boolean): Omit<ShapeLists, 'categoryId' | 'teamLine' | 'sittingMin'> {
  const b = businessId ?? '';
  const on = (s: BookingShape) => enabled && !!businessId && shape === s;
  const places = useQuery({ queryKey: shapeKeys.places(b), queryFn: () => listPlaces(b), enabled: on('place'), ...opts });
  const bands = useQuery({ queryKey: shapeKeys.bands(b), queryFn: () => getBands(b), enabled: on('place'), ...opts });
  const periods = useQuery({ queryKey: shapeKeys.periods(b), queryFn: () => listPeriods(b), enabled: on('business'), ...opts });
  const unitTypes = useQuery({ queryKey: shapeKeys.unitTypes(b), queryFn: () => listUnitTypes(b), enabled: on('unit'), ...opts });
  const programs = useQuery({ queryKey: shapeKeys.programs(b), queryFn: () => listPrograms(b), enabled: on('seat'), ...opts });
  const tables = useQuery({ queryKey: shapeKeys.tables(b), queryFn: () => listTables(b), enabled: on('table'), ...opts });
  const programList = on('seat') ? (programs.data ?? []) : [];
  const sessions = useQueries({ queries: programList.map((p) => ({ queryKey: shapeKeys.sessions(b, p.id), queryFn: () => listSessions(b, p.id), ...opts })) });

  switch (shape) {
    case 'place':
      return { places: places.data ?? [], bands: bands.data ?? [] };
    case 'business':
      return { periods: periods.data ?? [] };
    case 'unit':
      return { unitTypes: unitTypes.data ?? [] };
    case 'seat':
      return {
        programs: programList
          .map((p, i) => ({ p, s: sessions[i]?.data ?? [] }))
          .filter((x) => x.p.active && x.p.upcoming_sessions > 0)
          .map((x) => ({ weekly: x.s.some((s) => s.status === 'active' && !!s.series_id) })),
      };
    case 'table':
      return { tables: tables.data ?? [] };
    default:
      return {};
  }
}
